// Feature: M004-F0714. Tests: T-M004-0208, T-M004-0209.
//
// /app/tx/:key is a REDIRECT, not a page (design 3.1): "TX-3f9a1c2e" in a Core log is https://<host>/app/tx/TX-3f9a1c2e.
// It opens the shared history drawer through the SAME `?tx=&org=` URL on the Billing tab (or, for a viewer with no Billing tab,
// the Ledger members page). Organisation: `?org=` if given; else the viewer's only organisation; with several, a "Which
// organisation?" list. NEVER an automatic try across organisations (that is the enumeration the 404 rule prevents).
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { getMyEffectiveOrganisationPermissionsDetailed, userApi } from '../services/api';
import { hubMembershipOrganisations, selectHubOrganisations } from '../components/bookings-admin/BookingsHub';
import { hasBillingTabPermission } from '../components/bookings-admin/bookingsHubPermissions';
import { parseTxInput } from '../utils/transactionId';
import { TX_BAD_INPUT } from '../components/TxFinder';

const orgIdOf = (org) => String(org?.id ?? org?.organisation_id);

// Pure target builder (jest-tested). `billing` = the viewer has the Billing tab for that organisation.
export const txRedirectTarget = (key, orgId, billing) => {
  const search = new URLSearchParams();
  if (billing) search.set('tab', 'billing');
  search.set('tx', key);
  search.set('org', String(orgId));
  return billing
    ? { pathname: '/app/bookings', search: search.toString() }
    : { pathname: `/app/ledger/${encodeURIComponent(String(orgId))}/members`, search: search.toString() };
};

export default function TxRedirect() {
  const { key: rawKey } = useParams();
  const [searchParams] = useSearchParams();
  const parsed = parseTxInput(decodeURIComponent(rawKey || ''));
  const requestedOrg = searchParams.get('org');
  const [state, setState] = useState({ phase: 'loading', orgs: [], target: null });

  useEffect(() => {
    if (!parsed.ok) return undefined;
    let current = true;
    const resolve = async () => {
      let orgId = requestedOrg;
      let orgs = [];
      if (!orgId) {
        const response = await userApi.getMyOrganisations().catch(() => null);
        orgs = selectHubOrganisations(hubMembershipOrganisations(response));
        if (orgs.length === 1) orgId = orgIdOf(orgs[0]);
      }
      if (!orgId) return { phase: orgs.length ? 'choose' : 'none', orgs };
      const resolved = await getMyEffectiveOrganisationPermissionsDetailed(orgId).catch(() => null);
      return { phase: 'go', orgs, target: txRedirectTarget(parsed.key, orgId, hasBillingTabPermission(resolved?.permissions || {})) };
    };
    resolve().then((next) => { if (current) setState(next); });
    return () => { current = false; };
  }, [parsed.ok, parsed.key, requestedOrg]);

  if (!parsed.ok) return <section className="tx-redirect"><p role="alert" data-testid="tx-redirect-bad">{TX_BAD_INPUT}</p></section>;
  if (state.phase === 'go') return <Navigate replace to={state.target} />;
  if (state.phase === 'choose') {
    return (
      <section className="tx-redirect" data-testid="tx-redirect-choose">
        <h1>Which organisation?</h1>
        <p>{parsed.key} could be in more than one of your organisations. Choose where to look.</p>
        <ul>
          {state.orgs.map((org) => (
            <li key={orgIdOf(org)}><Link to={`/app/tx/${encodeURIComponent(parsed.key)}?org=${encodeURIComponent(orgIdOf(org))}`}>{org.name || `Organisation ${orgIdOf(org)}`}</Link></li>
          ))}
        </ul>
      </section>
    );
  }
  if (state.phase === 'none') return <section className="tx-redirect"><p role="status" data-testid="tx-redirect-none">You are not a member of an organisation, so there is nowhere to look for {parsed.key}.</p></section>;
  return <section className="tx-redirect"><p role="status">Opening {parsed.key}...</p></section>;
}
